import { useState, useEffect } from "react";
import { sendEmailVerification } from "firebase/auth";
import { auth } from "../lib/firebase";

const VERIFY_EMAIL_ERROR = "Verify your email address before joining a workspace";

export function JoinWorkspace({
  email,
  onJoined,
  onSignOut,
  onShowCreate,
}: {
  email: string;
  onJoined: (workspaceId: string, role: "partner") => void;
  onSignOut: () => void;
  onShowCreate: () => void;
}) {
  const [code, setCode] = useState(() => {
    // Check URL for ?code= parameter (Multi-Tenant invite links)
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      return params.get("code") || "";
    }
    return "";
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [verifyNotice, setVerifyNotice] = useState<string | null>(null);

  // Auto-join if code is in URL (Multi-Tenant invite link)
  useEffect(() => {
    if (code && typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      if (params.get("code") === code) {
        // Code came from URL, auto-join via new API
        handleJoinViaApi(code);
        // Clear URL parameter after attempting join
        params.delete("code");
        const newUrl = window.location.pathname + (params.toString() ? `?${params.toString()}` : "");
        window.history.replaceState({}, "", newUrl);
      }
    }
  }, []);

  /**
   * Join via new Multi-Tenant invite API
   * Uses /api/invites/validate endpoint with Firebase Auth UID
   */
  async function handleJoinViaApi(joinCode: string) {
    setLoading(true);
    setError(null);
    setNeedsVerification(false);
    setVerifyNotice(null);

    try {
      const user = auth.currentUser;
      if (!user) {
        setError("Authentication error. Please sign in again.");
        setLoading(false);
        return;
      }

      const response = await fetch("/api/invites/validate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await user.getIdToken()}`,
        },
        body: JSON.stringify({
          code: joinCode.trim(),
          userId: user.uid,
          userEmail: email,
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        if (response.status === 403 && result.error === VERIFY_EMAIL_ERROR) {
          setNeedsVerification(true);
        }
        setError(result.error || "Failed to join workspace");
        setLoading(false);
        return;
      }

      // Success - membership created via API
      onJoined(result.workspaceId, "partner");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Network error. Please try again.");
      setLoading(false);
    }
  }

  async function handleResendVerification() {
    setVerifyNotice(null);
    try {
      const user = auth.currentUser;
      if (!user) {
        setVerifyNotice("Authentication error. Please sign in again.");
        return;
      }
      await sendEmailVerification(user);
      setVerifyNotice("Verification email sent. Check your inbox, then click Try again.");
    } catch (e) {
      setVerifyNotice(e instanceof Error ? e.message : "Couldn't send the verification email. Please try again.");
    }
  }

  async function handleTryAgain() {
    try {
      // Pick up a verification completed in another tab: the token's
      // email_verified claim only updates after a reload + token refresh.
      await auth.currentUser?.reload();
      await auth.currentUser?.getIdToken(true);
    } catch {
      // Fall through; the join attempt below reports any real failure.
    }
    await handleJoin();
  }

  /**
   * Join with manual code entry via the Multi-Tenant invite API.
   *
   * track:workspace-member-email-key-cleanup — removed the old
   * relay_join_codes/email-keyed-doc fallback that used to live here. It
   * wrote relay_workspace_members/{email} directly via the client SDK,
   * which firestore.rules' composite-key `allow create` (requires
   * memberId == user_id + '_' + workspace_id and status == 'pending')
   * already rejects outright — and it was only reachable if
   * handleJoinViaApi threw synchronously, which it never does (it catches
   * its own errors). Effectively dead on both counts.
   */
  async function handleJoin() {
    const normalised = code.trim();
    if (!normalised) {
      setError("Please enter a join code.");
      return;
    }

    const user = auth.currentUser;
    if (!user) {
      setError("Authentication error. Please sign in again.");
      return;
    }

    await handleJoinViaApi(normalised);
  }

  return (
    <div
      className="h-screen w-screen flex items-center justify-center"
      style={{ background: "var(--bg)", color: "var(--text)" }}
    >
      <div
        className="flex flex-col items-center gap-4 px-6 py-10"
        style={{
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: 8,
          maxWidth: 420,
          width: "100%",
        }}
      >
        <span
          className="font-mono text-lg lowercase tracking-tight"
          style={{ color: "var(--text)" }}
        >
          relay
        </span>
        <div className="text-sm font-medium" style={{ color: "var(--text)" }}>
          Join a workspace
        </div>
        <div className="text-xs text-center" style={{ color: "var(--text2)" }}>
          Signed in as{" "}
          <span style={{ fontFamily: "var(--font-mono)" }}>{email}</span>
        </div>
        <input
          type="text"
          placeholder="Paste your invite code"
          value={code}
          // Invite codes are case-sensitive base64url: keep the typed case. Trim before
          // the length limit (a maxLength attribute would cut a padded paste short).
          onChange={e => setCode(e.target.value.trim().slice(0, 12))}
          onKeyDown={e => {
            if (e.key === "Enter" && !loading) handleJoin();
          }}
          disabled={loading}
          style={{
            width: "100%",
            padding: "8px 12px",
            background: "var(--bg)",
            border: "1px solid var(--border)",
            borderRadius: 6,
            color: "var(--text)",
            fontSize: 13,
            fontFamily: "var(--font-mono)",
            boxSizing: "border-box",
            letterSpacing: "0.05em",
          }}
        />
        <button
          onClick={handleJoin}
          disabled={loading || code.trim().length === 0}
          style={{
            background: "var(--accent)",
            color: "#fff",
            border: 0,
            borderRadius: 6,
            padding: "8px 18px",
            fontSize: 13,
            cursor: loading || code.trim().length === 0 ? "not-allowed" : "pointer",
            opacity: loading || code.trim().length === 0 ? 0.6 : 1,
            fontFamily: "inherit",
            width: "100%",
          }}
        >
          {loading ? "Joining…" : "Join Workspace"}
        </button>
        {error && (
          <div
            style={{
              fontSize: 11,
              color: "var(--tag-blocker)",
              textAlign: "center",
            }}
          >
            {error}
          </div>
        )}
        {needsVerification && (
          <div className="flex flex-col items-center gap-2" style={{ width: "100%" }}>
            <button
              type="button"
              onClick={handleResendVerification}
              style={{ background: "var(--accent)", color: "#fff", border: 0, borderRadius: 6, padding: "6px 14px", fontSize: 12, cursor: "pointer", fontFamily: "inherit", width: "100%" }}
            >
              Resend verification email
            </button>
            <button
              type="button"
              onClick={handleTryAgain}
              disabled={loading}
              style={{ background: "transparent", color: "var(--accent)", border: "1px solid var(--border)", borderRadius: 6, padding: "6px 14px", fontSize: 12, cursor: "pointer", fontFamily: "inherit", width: "100%" }}
            >
              Try again
            </button>
            {verifyNotice && (
              <div style={{ fontSize: 11, color: "var(--text2)", textAlign: "center" }}>{verifyNotice}</div>
            )}
          </div>
        )}
        <button
          onClick={onShowCreate}
          style={{
            background: "transparent",
            color: "var(--accent)",
            border: 0,
            padding: "4px 0",
            fontSize: 11,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          Create a workspace instead
        </button>
        <button
          onClick={onSignOut}
          style={{
            background: "transparent",
            color: "var(--text2)",
            border: 0,
            padding: "4px 0",
            fontSize: 11,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
